import React, { useState, useMemo } from 'react';
import { buildDisplayNameMap, buildPortfolioCategories } from '../utils/portfolioNames';

const COLOURS = {
    primary: '#072040',
    secondary: '#f0a500',
    success: '#91CF9B',
    info: '#599BE3',
    light: '#E7E6E6',
    white: '#ffffff',
    gray: '#6c757d',
};

const BAR_COLOURS = [COLOURS.primary, COLOURS.info, COLOURS.secondary, COLOURS.success, '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

// Hardcoded fallback — used only when registry hasn't loaded yet
const FALLBACK_DISPLAY_NAMES = {
    Cash: 'Cash',
    DDP: 'Drummond Dynamic Portfolio',
    DPM: 'Drummond Private Markets Portfolio',
    DPC: 'Drummond Private Income Portfolio',
    DS30: 'Drummond Strategic 30',
    DS50: 'Drummond Strategic 50',
    DS70: 'Drummond Strategic 70',
    DS90: 'Drummond Strategic 90',
    DS100: 'Drummond Strategic 100',
    DS100Plus: 'Drummond Strategic 100+',
    FirstChoiceConservative: 'First Choice Conservative',
    FirstChoiceModerate: 'First Choice Moderate',
    FirstChoiceBalanced: 'First Choice Balanced',
    FirstChoiceGrowth: 'First Choice Growth',
    FirstChoiceHighGrowth: 'First Choice High Growth',
    GovernmentBonds: 'Government Bonds',
    Credit: 'Credit',
    AustralianEquities: 'Australian Equities',
    GlobalEquitiesHedged: 'Global Equities (Hedged)',
    GlobalEquitiesUnhedged: 'Global Equities (Unhedged)',
    EmergingMarketEquities: 'Emerging Market Equities',
    HedgeFunds: 'Hedge Funds',
    REITs: 'REITs',
    Infrastructure: 'Infrastructure',
};

const FALLBACK_CATEGORIES = [
    {
        label: 'Strategic Series',
        keys: ['DS30', 'DS50', 'DS70', 'DS90', 'DS100', 'DS100Plus', 'DDP'],
    },
    {
        label: 'FirstChoice Series',
        keys: ['FirstChoiceConservative', 'FirstChoiceModerate', 'FirstChoiceBalanced', 'FirstChoiceGrowth', 'FirstChoiceHighGrowth'],
    },
    {
        label: 'Private Market',
        keys: ['DPM', 'DPC'],
    },
    // No Asset Classes fallback — sourced live from the SAC taxonomy via
    // buildPortfolioCategories. If the registry hasn't loaded yet the picker
    // shows portfolio categories only; SACs appear once the registry is in.
];

let _rowId = 0;

const PortfolioMixer = ({ availablePortfolios, portfolioRegistry, onCalculate, isLoading }) => {
    const [rows, setRows] = useState([{ id: ++_rowId, portfolioName: '', weight: 0 }]);

    const availableKeys = useMemo(
        () => new Set(availablePortfolios),
        [availablePortfolios]
    );

    // Build display name map and categories from registry (with fallback)
    const displayNames = useMemo(
        () => portfolioRegistry ? buildDisplayNameMap(portfolioRegistry) : FALLBACK_DISPLAY_NAMES,
        [portfolioRegistry]
    );
    const getDisplayName = (key) => displayNames[key] || key;

    const categories = useMemo(
        () => buildPortfolioCategories(portfolioRegistry, availableKeys) || FALLBACK_CATEGORIES,
        [portfolioRegistry, availableKeys]
    );

    const selectedNames = useMemo(
        () => new Set(rows.filter((r) => r.portfolioName).map((r) => r.portfolioName)),
        [rows]
    );

    const totalWeight = rows.reduce((s, r) => s + (r.weight || 0), 0);
    const isValidTotal = Math.abs(totalWeight - 100) < 0.5;

    const addRow = () => {
        setRows((prev) => [...prev, { id: ++_rowId, portfolioName: '', weight: 0 }]);
    };

    const removeRow = (id) => {
        setRows((prev) => prev.filter((r) => r.id !== id));
    };

    const updateRow = (id, field, value) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                if (field === 'weight') {
                    const num = parseFloat(value) || 0;
                    return { ...r, weight: Math.max(0, Math.min(100, num)) };
                }
                return { ...r, [field]: value };
            })
        );
    };

    const buildOptions = (currentRowName) => {
        return categories.map((cat) => {
            // buildPortfolioCategories already filters portfolio codes against
            // availableKeys (skipping codes without registered SAA). It does
            // NOT filter the Asset Classes group against availableKeys because
            // /available-portfolios only enumerates blender codes, never SACs.
            // Re-applying the filter here would silently strip the entire
            // taxonomy SAC list. Just dedup against already-selected rows.
            const options = cat.keys
                .filter((k) => k === currentRowName || !selectedNames.has(k));
            if (options.length === 0) return null;
            return (
                <optgroup key={cat.label} label={cat.label}>
                    {options.map((k) => (
                        <option key={k} value={k}>
                            {getDisplayName(k)}
                        </option>
                    ))}
                </optgroup>
            );
        }).filter(Boolean);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const weights = {};
        rows.forEach((r) => {
            if (r.portfolioName && r.weight > 0) {
                weights[r.portfolioName] = (weights[r.portfolioName] || 0) + r.weight / 100;
            }
        });

        if (Object.keys(weights).length === 0) {
            alert('Please select at least one portfolio and weight.');
            return;
        }

        onCalculate(weights);
    };

    return (
        <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: COLOURS.primary, color: COLOURS.white, border: 'none' }}>
                <h5 className="mb-0">
                    <i className="bi bi-pie-chart me-2"></i>Portfolio Composition
                </h5>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    {/* Stacked allocation bar */}
                    {rows.length > 0 && rows.some(r => r.portfolioName && r.weight > 0) && (
                        <div className="mb-3">
                            <div style={{ display: 'flex', height: '28px', borderRadius: '6px', overflow: 'hidden', border: `1px solid ${COLOURS.light}` }}>
                                {rows.filter(r => r.portfolioName && r.weight > 0).map((row, i) => (
                                    <div
                                        key={row.id}
                                        style={{
                                            width: `${row.weight}%`,
                                            backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length],
                                            transition: 'width 0.3s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}
                                        title={`${getDisplayName(row.portfolioName)}: ${row.weight}%`}
                                    >
                                        {row.weight >= 8 && (
                                            <span style={{ color: COLOURS.white, fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                                {row.weight}%
                                            </span>
                                        )}
                                    </div>
                                ))}
                                {totalWeight < 100 && (
                                    <div style={{ flex: 1, backgroundColor: COLOURS.light, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <span style={{ fontSize: '11px', color: COLOURS.gray }}>
                                            {(100 - totalWeight).toFixed(0)}% unallocated
                                        </span>
                                    </div>
                                )}
                            </div>
                            {/* Legend */}
                            <div className="d-flex flex-wrap gap-3 mt-2">
                                {rows.filter(r => r.portfolioName && r.weight > 0).map((row, i) => (
                                    <div key={row.id} className="d-flex align-items-center">
                                        <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length], display: 'inline-block', marginRight: 6 }}></span>
                                        <small className="text-muted">{getDisplayName(row.portfolioName)}</small>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Table-style rows */}
                    {rows.length > 0 && (
                        <div className="table-responsive mb-3">
                            <table className="table table-sm align-middle mb-0">
                                <thead>
                                    <tr style={{ backgroundColor: COLOURS.light }}>
                                        <th style={{ width: '45%' }}>Portfolio</th>
                                        <th style={{ width: '30%' }}>Allocation (%)</th>
                                        <th style={{ width: '15%' }} className="text-center">Weight</th>
                                        <th style={{ width: '10%' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, i) => (
                                        <tr key={row.id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <span style={{ width: 4, height: 28, borderRadius: 2, backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length], display: 'inline-block', marginRight: 10, flexShrink: 0 }}></span>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={row.portfolioName}
                                                        onChange={(e) => updateRow(row.id, 'portfolioName', e.target.value)}
                                                        style={{ border: 'none', backgroundColor: 'transparent', boxShadow: 'none', paddingLeft: 0 }}
                                                    >
                                                        <option value="">Select portfolio...</option>
                                                        {buildOptions(row.portfolioName)}
                                                    </select>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ position: 'relative' }}>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        min="0"
                                                        max="100"
                                                        value={row.weight || ''}
                                                        placeholder="0"
                                                        onChange={(e) => updateRow(row.id, 'weight', e.target.value)}
                                                        style={{ paddingRight: '28px' }}
                                                    />
                                                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: COLOURS.gray, fontSize: '13px', pointerEvents: 'none' }}>%</span>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: 6,
                                                        backgroundColor: COLOURS.light,
                                                        borderRadius: 3,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: `${Math.min(row.weight || 0, 100)}%`,
                                                            height: '100%',
                                                            backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length],
                                                            borderRadius: 3,
                                                            transition: 'width 0.3s ease',
                                                        }}
                                                    ></div>
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm"
                                                    onClick={() => removeRow(row.id)}
                                                    title="Remove"
                                                    style={{ color: '#dc3545', padding: '2px 6px' }}
                                                >
                                                    <i className="bi bi-x-lg"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Empty state */}
                    {rows.length === 0 && (
                        <div className="text-center py-4 mb-3" style={{ backgroundColor: COLOURS.light, borderRadius: '8px' }}>
                            <i className="bi bi-pie-chart" style={{ fontSize: '32px', color: COLOURS.gray }}></i>
                            <p className="text-muted mt-2 mb-0">No portfolios added yet. Click below to start building.</p>
                        </div>
                    )}

                    {/* Add Portfolio button */}
                    <div className="text-center mb-3">
                        <button
                            type="button"
                            className="btn text-white"
                            style={{ backgroundColor: COLOURS.primary }}
                            onClick={addRow}
                        >
                            <i className="bi bi-plus-circle me-1"></i> Add Portfolio
                        </button>
                    </div>

                    {/* Total Weight Display */}
                    <div className={`alert ${isValidTotal ? 'alert-success' : 'alert-warning'} mb-3 py-2`}>
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold">Total Allocation:</span>
                            <span className="fs-5 fw-bold">{totalWeight.toFixed(1)}%</span>
                        </div>
                        {totalWeight > 100 && (
                            <small className="d-block mt-1">Total exceeds 100%</small>
                        )}
                        {totalWeight < 100 && totalWeight > 0 && (
                            <small className="d-block mt-1">Total must equal 100%</small>
                        )}
                    </div>

                    <button className="btn btn-primary w-100 shadow-lg" type="submit" disabled={isLoading || !isValidTotal}>
                        {isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Calculating...
                            </>
                        ) : (
                            'Calculate Blend'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default PortfolioMixer;
